import { useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import { MAX_VISIBLE_MARKERS } from '../constants';
import { getEventCategoryVisual } from '../../../../config/eventCategoryVisuals';
import { buildEventCategoryMarkerIcon } from '../eventCategoryMarkerIcon';

function postDebugLog(payload) {
  fetch('http://127.0.0.1:7543/ingest/6827e7d0-9c41-4ca4-995b-041f97954400', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Debug-Session-Id': 'df8df3',
    },
    body: JSON.stringify({
      sessionId: 'df8df3',
      timestamp: Date.now(),
      ...payload,
    }),
  }).catch(() => {});
}

function scheduleMarkerUiEffect(marker, className, { waitMs, listenAnimationEnd }) {
  requestAnimationFrame(() => {
    const el = marker.getElement?.();
    if (!el) return;
    const inner = el.querySelector?.('.hb-marker-icon-wrap') || el.querySelector?.('.hb-marker-icon-inner');
    const target = inner || el;

    if (className === 'hb-event-marker--zoom-snap') {
      if (
        target.classList.contains('hb-event-marker--drop-in') ||
        target.classList.contains('hb-event-marker--select-pulse')
      ) {
        return;
      }
    }

    if (className === 'hb-event-marker--drop-in' || className === 'hb-event-marker--select-pulse') {
      target.classList.remove('hb-event-marker--zoom-snap');
    }

    target.classList.add(className);
    let cleared = false;
    const done = () => {
      if (cleared) return;
      cleared = true;
      target.classList.remove(className);
    };
    const t = window.setTimeout(done, waitMs);
    if (listenAnimationEnd) {
      target.addEventListener(
        'animationend',
        () => {
          window.clearTimeout(t);
          done();
        },
        { once: true }
      );
    }
  });
}

/** Klastry: rozmiar ikony ~√(liczba), kotwica na środku; HTML pod własne style (badge). */
function createHbClusterDivIcon(cluster) {
  const childCount = cluster.getChildCount();
  let tier = 'small';
  if (childCount >= 100) {
    tier = 'large';
  } else if (childCount >= 10) {
    tier = 'medium';
  }
  const raw = 48 + Math.sqrt(childCount) * 3.5;
  const size = Math.round(Math.min(86, Math.max(48, raw)));
  const half = Math.round(size / 2);
  return new L.DivIcon({
    html: `<div class="hb-cluster-body"><span class="hb-cluster-count">${childCount}</span></div>`,
    className: `marker-cluster marker-cluster-${tier} hb-cluster-marker`,
    iconSize: new L.Point(size, size),
    iconAnchor: new L.Point(half, half),
  });
}

/**
 * Markery wydarzeń na mapie Leaflet + klastrowanie (leaflet.markercluster)
 * @param {React.MutableRefObject<{ lat: number; lng: number; radiusM: number } | null>} [radiusFilterRef] — gdy ustawione, tylko markery w promieniu od (lat,lng) w metrach
 */
export function useMapMarkers(mapRef, onMarkerClick, radiusFilterRef) {
  const markersRef = useRef([]);
  const clusterGroupRef = useRef(null);
  const selectedMarkerRef = useRef(null);
  const highlightCircleRef = useRef(null);
  const lastVisibleMarkerIdsRef = useRef(null);
  const lastMarkersSignatureRef = useRef(null);

  const buildSvgIconDataUrl = useCallback(
    (eventType = 'other', isFree = false, isSelected = false, pulseSeed = null) => {
      return buildEventCategoryMarkerIcon(L, eventType, isFree, isSelected, 'hb-leaflet-marker-icon', pulseSeed);
    },
    []
  );

  const highlightMarker = useCallback(
    (eventId) => {
      const map = mapRef.current;
      if (!map) return;

      const marker = markersRef.current.find((m) => m.eventData?.id === eventId);
      if (!marker) return;

      if (selectedMarkerRef.current && selectedMarkerRef.current !== marker) {
        const prevEvent = selectedMarkerRef.current.eventData;
        if (prevEvent) {
          selectedMarkerRef.current.setIcon(
            buildSvgIconDataUrl(prevEvent.event_type || 'other', prevEvent.is_free, false, prevEvent.id)
          );
          selectedMarkerRef.current.setZIndexOffset(0);
        }
      }

      if (highlightCircleRef.current) {
        map.removeLayer(highlightCircleRef.current);
        highlightCircleRef.current = null;
      }

      const event = marker.eventData;
      marker.setIcon(buildSvgIconDataUrl(event.event_type || 'other', event.is_free, true, event.id));
      marker.setZIndexOffset(1000);
      scheduleMarkerUiEffect(marker, 'hb-event-marker--select-pulse', {
        waitMs: 800,
        listenAnimationEnd: true,
      });

      const fill = getEventCategoryVisual(event.event_type).color;
      const circle = L.circle(marker.getLatLng(), {
        radius: 22,
        color: fill,
        weight: 2,
        opacity: 0.5,
        fillColor: fill,
        fillOpacity: 0.12,
        className: 'hb-event-marker-highlight-circle',
        interactive: false,
      }).addTo(map);
      highlightCircleRef.current = circle;
      selectedMarkerRef.current = marker;
    },
    [mapRef, buildSvgIconDataUrl]
  );

  const unhighlightMarker = useCallback(() => {
    const map = mapRef.current;
    if (selectedMarkerRef.current) {
      const event = selectedMarkerRef.current.eventData;
      if (event) {
        selectedMarkerRef.current.setIcon(
          buildSvgIconDataUrl(event.event_type || 'other', event.is_free, false, event.id)
        );
        selectedMarkerRef.current.setZIndexOffset(0);
      }
      selectedMarkerRef.current = null;
    }
    if (highlightCircleRef.current && map) {
      map.removeLayer(highlightCircleRef.current);
      highlightCircleRef.current = null;
    }
  }, [mapRef, buildSvgIconDataUrl]);

  const updateVisibleMarkers = useCallback(() => {
    const perfStart = performance.now();
    const map = mapRef.current;
    if (!map || !clusterGroupRef.current || markersRef.current.length === 0) {
      return { hiddenCount: 0, visibleEvents: [] };
    }

    const bounds = map.getBounds();
    if (!bounds) {
      return { hiddenCount: 0, visibleEvents: [] };
    }

    const rf = radiusFilterRef?.current;
    const hasRadiusFilter =
      rf &&
      Number.isFinite(rf.lat) &&
      Number.isFinite(rf.lng) &&
      Number.isFinite(rf.radiusM) &&
      rf.radiusM > 0;
    const filterCenter = hasRadiusFilter ? L.latLng(rf.lat, rf.lng) : null;
    const radiusM = hasRadiusFilter ? rf.radiusM : 0;

    const visibleMarkers = markersRef.current.filter((marker) => {
      const ll = marker.getLatLng();
      if (!bounds.contains(ll)) return false;
      if (filterCenter && radiusM > 0) {
        return filterCenter.distanceTo(ll) <= radiusM;
      }
      return true;
    });

    let markersToShow = visibleMarkers;
    let hiddenCount = 0;

    if (visibleMarkers.length > MAX_VISIBLE_MARKERS) {
      markersToShow = visibleMarkers
        .sort((a, b) => {
          const eventA = a.eventData;
          const eventB = b.eventData;
          if (!eventA || !eventB) return 0;
          const dateA = new Date(`${eventA.event_date}T${eventA.start_time || '00:00'}`);
          const dateB = new Date(`${eventB.event_date}T${eventB.start_time || '00:00'}`);
          return dateA - dateB;
        })
        .slice(0, MAX_VISIBLE_MARKERS);

      hiddenCount = visibleMarkers.length - MAX_VISIBLE_MARKERS;
    }

    clusterGroupRef.current.clearLayers();
    if (markersToShow.length > 0) {
      markersToShow.forEach((m) => clusterGroupRef.current.addLayer(m));
    }

    const nextIds = new Set(
      markersToShow.map((m) => m.eventData?.id).filter((id) => id != null)
    );
    const prevIds = lastVisibleMarkerIdsRef.current;
    let newlyAddedVisible = 0;
    if (prevIds) {
      markersToShow.forEach((m) => {
        const id = m.eventData?.id;
        if (id != null && !prevIds.has(id)) {
          newlyAddedVisible += 1;
          scheduleMarkerUiEffect(m, 'hb-event-marker--drop-in', {
            waitMs: 1250,
            listenAnimationEnd: false,
          });
        }
      });
    }
    lastVisibleMarkerIdsRef.current = nextIds;
    const elapsedMs = Math.round((performance.now() - perfStart) * 100) / 100;

    // #region agent log
    postDebugLog({
      runId: 'baseline',
      hypothesisId: 'H1',
      location: 'useMapMarkers.js:updateVisibleMarkers',
      message: 'Visible marker recalculation and cluster relayer completed',
      data: {
        totalMarkers: markersRef.current.length,
        visibleMarkersInBounds: visibleMarkers.length,
        markersRendered: markersToShow.length,
        hiddenCount,
        newlyAddedVisible,
        elapsedMs,
      },
    });
    // #endregion

    return {
      hiddenCount,
      visibleEvents: markersToShow
        .map((marker) => marker.eventData)
        .filter(Boolean),
    };
  }, [mapRef, radiusFilterRef]);

  const createMarkers = useCallback(
    (eventsWithCoords) => {
      const perfStart = performance.now();
      const map = mapRef.current;
      if (!map || eventsWithCoords.length === 0) {
        lastMarkersSignatureRef.current = null;
        return;
      }

      const nextSignature = eventsWithCoords
        .map((e) => `${e.id}:${e.coordinates?.lat ?? 'x'}:${e.coordinates?.lng ?? 'x'}:${e.event_type ?? 'other'}:${e.is_free ? 1 : 0}`)
        .sort()
        .join('|');

      if (lastMarkersSignatureRef.current === nextSignature && markersRef.current.length > 0) {
        // #region agent log
        postDebugLog({
          runId: 'post-fix',
          hypothesisId: 'H2',
          location: 'useMapMarkers.js:createMarkers',
          message: 'Skipped marker rebuild because dataset signature is unchanged',
          data: {
            eventsCount: eventsWithCoords.length,
            markersCount: markersRef.current.length,
          },
        });
        // #endregion
        return;
      }

      unhighlightMarker();
      lastVisibleMarkerIdsRef.current = null;
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
        clusterGroupRef.current = null;
      }
      markersRef.current.forEach((m) => {
        try {
          map.removeLayer(m);
        } catch {
          /* już usunięty z warstwy */
        }
      });
      markersRef.current = [];

      const allEvents = eventsWithCoords.filter((e) => e.coordinates);
      const byLocation = new Map();
      allEvents.forEach((event) => {
        const key = `${event.coordinates.lat.toFixed(6)},${event.coordinates.lng.toFixed(6)}`;
        if (!byLocation.has(key)) byLocation.set(key, []);
        byLocation.get(key).push(event);
      });

      const markers = [];
      byLocation.forEach((eventsAtLocation) => {
        if (eventsAtLocation.length === 1) {
          const event = eventsAtLocation[0];
          const m = L.marker([event.coordinates.lat, event.coordinates.lng], {
            icon: buildSvgIconDataUrl(event.event_type || 'other', event.is_free, false, event.id),
            title: event.title,
          });
          m.eventData = event;
          m.on('click', () => onMarkerClick(event));
          markers.push(m);
        } else {
          const offsetDistance = 0.0001;
          eventsAtLocation.forEach((event, index) => {
            const angle = (index * 2 * Math.PI) / eventsAtLocation.length;
            const offsetLat = event.coordinates.lat + offsetDistance * Math.cos(angle);
            const offsetLng = event.coordinates.lng + offsetDistance * Math.sin(angle);
            const m = L.marker([offsetLat, offsetLng], {
              icon: buildSvgIconDataUrl(event.event_type || 'other', event.is_free, false, event.id),
              title: `${event.title} (${eventsAtLocation.length} wydarzeń w tym miejscu)`,
            });
            m.eventData = event;
            m.originalPosition = event.coordinates;
            m.on('click', () => onMarkerClick(event));
            markers.push(m);
          });
        }
      });

      markersRef.current = markers;
      lastMarkersSignatureRef.current = nextSignature;
      const elapsedMs = Math.round((performance.now() - perfStart) * 100) / 100;

      // #region agent log
      postDebugLog({
        runId: 'post-fix',
        hypothesisId: 'H2',
        location: 'useMapMarkers.js:createMarkers',
        message: 'Markers were rebuilt from event dataset',
        data: {
          inputEvents: eventsWithCoords.length,
          eventsWithCoords: allEvents.length,
          uniqueLocations: byLocation.size,
          builtMarkers: markers.length,
          elapsedMs,
        },
      });
      // #endregion

      if (markers.length > 0) {
        const cluster = L.markerClusterGroup({
          maxClusterRadius: 64,
          disableClusteringAtZoom: 16,
          spiderfyOnMaxZoom: true,
          iconCreateFunction: createHbClusterDivIcon,
        });
        clusterGroupRef.current = cluster;
        cluster.addTo(map);
      }
    },
    [mapRef, onMarkerClick, buildSvgIconDataUrl, unhighlightMarker]
  );

  const playZoomMarkersFx = useCallback(() => {
    const map = mapRef.current;
    if (!map || markersRef.current.length === 0) return;
    const b = map.getBounds();
    if (!b) return;

    markersRef.current.forEach((m) => {
      if (!m?.getLatLng || !b.contains(m.getLatLng())) return;
      const el = m.getElement?.();
      if (!el?.querySelector?.('.hb-marker-icon-wrap')) return;
      scheduleMarkerUiEffect(m, 'hb-event-marker--zoom-snap', {
        waitMs: 520,
        listenAnimationEnd: true,
      });
    });
  }, [mapRef]);

  const clearMarkers = useCallback(() => {
    const map = mapRef.current;
    unhighlightMarker();
    lastVisibleMarkerIdsRef.current = null;
    if (clusterGroupRef.current && map) {
      map.removeLayer(clusterGroupRef.current);
      clusterGroupRef.current = null;
    }
    markersRef.current.forEach((m) => {
      try {
        map?.removeLayer(m);
      } catch {
        /* */
      }
    });
    markersRef.current = [];
    lastMarkersSignatureRef.current = null;
  }, [mapRef, unhighlightMarker]);

  return {
    markersRef,
    clustererRef: clusterGroupRef,
    createMarkers,
    updateVisibleMarkers,
    clearMarkers,
    createMarkerIcon: buildSvgIconDataUrl,
    highlightMarker,
    unhighlightMarker,
    playZoomMarkersFx,
  };
}

export default useMapMarkers;
